import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

function getArchitectKey() {
  return (typeof window !== "undefined" && window.localStorage.getItem("nx_architect_key")) || "";
}

function GiftCard({ row, onPromote, onArchive, busyId }) {
  const p = row.payload || {};
  const ts = row.timestamp ? new Date(row.timestamp).toLocaleString() : "";
  const status = p.status || "unknown";
  const busy = busyId === row.id;
  return (
    <article
      data-testid={`gift-${row.id}`}
      className={`rounded-xl border p-4 ${
        status === "pending_review" ? "border-emerald-400/40 bg-emerald-500/[0.04]" :
        status === "promoted"       ? "border-slate-700/60 bg-slate-900/40 opacity-70" :
                                      "border-slate-800 bg-slate-950/40 opacity-50"
      }`}
    >
      <header className="mb-2 flex flex-wrap items-center gap-2">
        <span className="rounded-full border border-emerald-400/40 bg-emerald-500/15 px-2 py-0.5 text-[11px] font-semibold text-emerald-300">
          seed · {p.seed_id || "?"}
        </span>
        <span className="text-sm font-semibold text-slate-100">{p.seed_name || "Unknown Seed"}</span>
        <span className="text-xs text-slate-500">·</span>
        <span className="text-xs text-slate-400">{ts}</span>
        <span className={`ml-auto rounded px-2 py-0.5 text-[11px] font-semibold uppercase ${
          status === "pending_review" ? "bg-amber-500/20 text-amber-200" :
          status === "promoted"       ? "bg-emerald-500/20 text-emerald-300" :
                                        "bg-slate-700/60 text-slate-400"
        }`}>
          {status.replace("_", " ")}
        </span>
      </header>
      <h3 className="mb-1 text-base font-semibold text-slate-100">{p.gift_title || "(no title)"}</h3>
      {p.summary && <p className="mb-2 text-sm italic text-slate-400">{p.summary}</p>}
      <div className="mb-3 whitespace-pre-wrap rounded bg-slate-950/60 p-3 text-xs leading-relaxed text-slate-300">
        {(p.content || "").slice(0, 1200)}
        {(p.content || "").length > 1200 ? "…" : ""}
      </div>
      <div className="mb-3 flex flex-wrap gap-3 text-[11px] text-slate-500">
        {p.confidence !== undefined && p.confidence !== null && (
          <span>confidence · <b className="text-slate-300">{p.confidence}%</b></span>
        )}
        {Array.isArray(p.core_impact) && p.core_impact.length > 0 && (
          <span>core_impact · <b className="text-slate-300">{p.core_impact.join(", ")}</b></span>
        )}
        {p.suggested_application && (
          <span>application · <b className="text-slate-300">{p.suggested_application}</b></span>
        )}
        {p.promoted_to && (
          <span>→ knowledge_node · <b className="text-emerald-300">{p.promoted_to}</b></span>
        )}
      </div>
      {status === "pending_review" && (
        <div className="flex gap-2">
          <button
            data-testid={`promote-${row.id}`}
            onClick={() => onPromote(row.id)}
            disabled={busy}
            className="rounded bg-emerald-500 px-3 py-1.5 text-xs font-semibold text-slate-950 hover:bg-emerald-400 disabled:opacity-50"
          >
            {busy ? "Promoting…" : "Promote to library"}
          </button>
          <button
            data-testid={`archive-${row.id}`}
            onClick={() => onArchive(row.id)}
            disabled={busy}
            className="rounded border border-slate-600 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800/80 disabled:opacity-50"
          >
            Archive
          </button>
        </div>
      )}
    </article>
  );
}

export default function PendingGiftsPanel() {
  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [tab, setTab]         = useState("pending");
  const [loading, setLoading] = useState(false);
  const [err, setErr]         = useState("");
  const [busyId, setBusyId]   = useState("");

  const load = useCallback(async () => {
    const key = getArchitectKey();
    if (!key) {
      setErr("Architect key required (set in Settings).");
      return;
    }
    setLoading(true);
    setErr("");
    try {
      const headers = { "X-Architect-Key": key };
      const [a, b] = await Promise.all([
        axios.get(`${API}/federation/gifts/pending`, { headers }),
        axios.get(`${API}/federation/gifts/history`, { headers, params: { limit: 50 } }),
      ]);
      setPending(a.data?.gifts || []);
      setHistory(b.data?.gifts || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Load failed.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function promote(id) {
    const key = getArchitectKey();
    if (!key) return setErr("Architect key required.");
    setBusyId(id);
    try {
      await axios.post(`${API}/federation/gifts/${id}/promote`, {}, { headers: { "X-Architect-Key": key } });
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Promote failed.");
    } finally {
      setBusyId("");
    }
  }

  async function archive(id) {
    const key = getArchitectKey();
    if (!key) return setErr("Architect key required.");
    setBusyId(id);
    try {
      await axios.post(`${API}/federation/gifts/${id}/archive`, {}, { headers: { "X-Architect-Key": key } });
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Archive failed.");
    } finally {
      setBusyId("");
    }
  }

  const rows = tab === "pending" ? pending : history;

  return (
    <section className="rounded-2xl border border-slate-700/60 bg-slate-900/40 p-5 backdrop-blur-sm" data-testid="pending-gifts-panel">
      <header className="mb-4 flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-semibold text-slate-100">Pending Gifts · Seed Inbox</h2>
        <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-semibold text-emerald-300" data-testid="pending-count">
          {pending.length} pending
        </span>
        <div className="ml-auto flex gap-1">
          <button
            data-testid="gifts-tab-pending"
            onClick={() => setTab("pending")}
            className={`rounded px-3 py-1 text-xs ${tab === "pending" ? "bg-amber-500/15 text-amber-200" : "text-slate-400 hover:bg-slate-800/80"}`}
          >
            Pending ({pending.length})
          </button>
          <button
            data-testid="gifts-tab-history"
            onClick={() => setTab("history")}
            className={`rounded px-3 py-1 text-xs ${tab === "history" ? "bg-amber-500/15 text-amber-200" : "text-slate-400 hover:bg-slate-800/80"}`}
          >
            History ({history.length})
          </button>
          <button
            data-testid="gifts-refresh"
            onClick={load}
            disabled={loading}
            className="rounded border border-slate-600 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800/80 disabled:opacity-50"
          >
            {loading ? "…" : "↻"}
          </button>
        </div>
      </header>

      {err && (
        <div className="mb-3 rounded border border-rose-500/40 bg-rose-500/10 p-2 text-xs text-rose-300" data-testid="gifts-error">
          {err}
        </div>
      )}

      <div className="space-y-3">
        {rows.length === 0 && !loading && (
          <div className="rounded border border-slate-700/60 p-4 text-center text-xs text-slate-500" data-testid="gifts-empty">
            {tab === "pending"
              ? "Inbox clear · no seed gifts awaiting review."
              : "No gifts in the review log yet."}
          </div>
        )}
        {rows.map((row) => (
          <GiftCard
            key={row.id}
            row={row}
            onPromote={promote}
            onArchive={archive}
            busyId={busyId}
          />
        ))}
      </div>

      <footer className="mt-4 text-[11px] text-slate-500">
        Gifts arrive via <code className="text-slate-400">POST /api/federation/seed/write/gift</code> from
        any Sovereign Seed. Promoted gifts mint a new <code className="text-slate-400">knowledge_node</code>;
        archived gifts stay in the ledger but never enter the library.
      </footer>
    </section>
  );
}
